/**
 * Day Change Calculator
 *
 * Frontend utility for calculating day change values (cd, ca) for flight schedules.
 * Aligns with backend validation rules.
 *
 * Business Rules:
 * - First station departure always has cd = 0
 * - Day changes calculated using UTC-aware time comparison
 * - Subsequent legs: cd = previous leg's ca
 * - All cd/ca values must be 0-7
 */

import moment from 'moment-timezone';

interface AirportInfo {
  iataAirportCode: string;
  utcVariant: string;
}

export interface Station {
  departureStation: string;
  std: string;
  cd?: number;
  utcLocalTimeVariationDep?: string;
  arrivalStation: string;
  sta: string;
  ca?: number;
  utcLocalTimeVariationArr?: string;
  [key: string]: any; // Allow other fields
}

/**
 * Validates IATA airport code format
 * @param code - Airport code to validate
 * @returns true if valid (3 uppercase letters)
 */
export function isValidIATA(code: string): boolean {
  return /^[A-Z]{3}$/.test(code);
}

/**
 * Validates time format (HHMM)
 * @param time - Time to validate
 * @returns true if valid (0000-2359)
 */
export function isValidTime(time: string): boolean {
  if (!/^\d{4}$/.test(time)) return false;
  const hours = parseInt(time.substring(0, 2), 10);
  const minutes = parseInt(time.substring(2, 4), 10);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

/**
 * Formats UTC variation to standard format
 * @param utcVariation - UTC offset (e.g., "+08", "+0800")
 * @returns Formatted UTC variation (e.g., "+0800")
 */
export function formatUtcVariation(utcVariation: string): string {
  if (!utcVariation) return '+0000';

  // Remove any whitespace
  let cleaned = utcVariation.trim();

  // Ensure it starts with + or -
  if (!/^[+-]/.test(cleaned)) {
    cleaned = '+' + cleaned;
  }

  // Extract sign and digits
  const sign = cleaned[0];
  const digits = cleaned.substring(1).replace(/\D/g, '');

  // Pad to 4 digits
  const padded = digits.padStart(4, '0');

  return sign + padded;
}

/**
 * Calculate day change between departure and arrival
 * Uses UTC-aware calculation to handle timezone crossings
 *
 * @param departureTime - Departure time in HHMM format (e.g., "2320")
 * @param arrivalTime - Arrival time in HHMM format (e.g., "0235")
 * @param departureUtcVariation - Departure UTC offset (e.g., "+0800")
 * @param arrivalUtcVariation - Arrival UTC offset (e.g., "+0400")
 * @returns Day change (0 = same day, 1 = next day, etc.)
 */
export function calculateDayChange(
  departureTime: string,
  arrivalTime: string,
  departureUtcVariation: string,
  arrivalUtcVariation: string
): number {
  // Validate inputs
  if (!isValidTime(departureTime) || !isValidTime(arrivalTime)) {
    return 0;
  }

  // Format UTC variations
  const depUtc = formatUtcVariation(departureUtcVariation);
  const arrUtc = formatUtcVariation(arrivalUtcVariation);

  // Parse times
  const depHour = parseInt(departureTime.substring(0, 2), 10);
  const depMin = parseInt(departureTime.substring(2, 4), 10);
  const arrHour = parseInt(arrivalTime.substring(0, 2), 10);
  const arrMin = parseInt(arrivalTime.substring(2, 4), 10);

  // Create moment objects in their respective timezones
  // Use a reference date (doesn't matter which, we only care about time difference)
  const referenceDate = '2026-01-01';

  // Convert UTC offset to timezone string (e.g., "+0800" -> "Etc/GMT-8")
  // Note: Etc/GMT has inverted signs
  const depTzOffset = parseInt(depUtc.substring(1, 3), 10) * (depUtc[0] === '+' ? -1 : 1);
  const arrTzOffset = parseInt(arrUtc.substring(1, 3), 10) * (arrUtc[0] === '+' ? -1 : 1);

  const depTz = `Etc/GMT${depTzOffset >= 0 ? '+' : ''}${depTzOffset}`;
  const arrTz = `Etc/GMT${arrTzOffset >= 0 ? '+' : ''}${arrTzOffset}`;

  // Create departure moment
  const departureMoment = moment.tz(referenceDate, depTz)
    .hour(depHour)
    .minute(depMin)
    .second(0);

  // Create arrival moment (start with same day)
  let arrivalMoment = moment.tz(referenceDate, arrTz)
    .hour(arrHour)
    .minute(arrMin)
    .second(0);

  // Simple rule: If arrival local time < departure local time, it's next day
  const depLocalMinutes = depHour * 60 + depMin;
  const arrLocalMinutes = arrHour * 60 + arrMin;

  let dayChange = 0;

  // If arrival time is earlier than departure time (in local time), it crossed midnight
  if (arrLocalMinutes < depLocalMinutes) {
    dayChange = 1;
  }

  // Additional check: For very long flights spanning multiple days
  // Convert to UTC to check if it's 24+ hours
  const depUtcMoment = departureMoment.clone().utc();
  let arrUtcMoment = arrivalMoment.clone().utc();

  // If arrival UTC is before departure UTC, add 1 day to arrival for calculation
  if (arrUtcMoment.isBefore(depUtcMoment)) {
    arrivalMoment.add(1, 'day');
    arrUtcMoment = arrivalMoment.clone().utc();
  }

  // Calculate hour difference for multi-day flights
  const hoursDiff = arrUtcMoment.diff(depUtcMoment, 'hours');

  // If flight is 24+ hours, add extra days
  if (hoursDiff >= 24) {
    const extraDays = Math.floor(hoursDiff / 24);
    dayChange += extraDays;
  }

  // Clamp to valid range (0-7)
  return Math.max(0, Math.min(7, dayChange));
}

/**
 * Calculate day changes for all stations in a flight
 * Enriches stations with cd, ca, and UTC variation fields
 *
 * @param stations - Array of station objects
 * @param airports - Map of airport IATA codes to airport data
 * @returns Enriched stations with calculated cd/ca values
 */
export function calculateStationDayChanges(
  stations: Station[],
  airports: Record<string, AirportInfo>
): Station[] {
  if (!stations || stations.length === 0) return [];

  const enrichedStations: Station[] = [];

  for (let i = 0; i < stations.length; i++) {
    const station = { ...stations[i] };

    // Get airport data
    const depAirport = airports[station.departureStation];
    const arrAirport = airports[station.arrivalStation];

    // Set UTC variations from airport data
    if (depAirport) {
      station.utcLocalTimeVariationDep = formatUtcVariation(depAirport.utcVariant);
    }
    if (arrAirport) {
      station.utcLocalTimeVariationArr = formatUtcVariation(arrAirport.utcVariant);
    }

    // Business Rule: First station always has cd = 0
    if (i === 0) {
      station.cd = 0;
    } else {
      // Subsequent stations: cd = previous station's ca
      station.cd = enrichedStations[i - 1].ca || 0;
    }

    // Calculate arrival day change
    const dayChange = calculateDayChange(
      station.std,
      station.sta,
      station.utcLocalTimeVariationDep || '+0000',
      station.utcLocalTimeVariationArr || '+0000'
    );

    // ca = cd + day change within this leg
    station.ca = (station.cd || 0) + dayChange;

    // Ensure ca stays within valid range (0-7)
    station.ca = Math.max(0, Math.min(7, station.ca));

    enrichedStations.push(station);
  }

  return enrichedStations;
}

/**
 * Simplified version that mutates the stations array
 * (for backward compatibility with existing code)
 */
export function calculateAllDayChanges(stations: any[]): void {
  if (!stations || stations.length === 0) return;

  for (let i = 0; i < stations.length; i++) {
    const station = stations[i];

    // First station departure always has cd = 0
    if (i === 0) {
      station.cd = 0;
    } else {
      // Subsequent stations: cd = previous station's ca
      station.cd = stations[i - 1].ca || 0;
    }

    // Calculate arrival day change using UTC-aware calculation
    const dayChange = calculateDayChange(
      station.std || '',
      station.sta || '',
      station.utcLocalTimeVariationDep || '+0000',
      station.utcLocalTimeVariationArr || '+0000'
    );

    // ca = cd + day change within this leg
    station.ca = (station.cd || 0) + dayChange;

    // Ensure ca stays within valid range (0-7)
    station.ca = Math.max(0, Math.min(7, station.ca));
  }
}
